import clamp from 'lodash/clamp'

import { EMPTY_OBJECT } from '../../constants/common'
import {
  PAGINATION_DEFAULT_PAGE_SIZE,
  PAGINATION_PAGE_PARAM,
  PAGINATION_PAGE_SIZE_PARAM,
  PAGINATION_PAGE_SIZES,
} from '../../constants/pagination'
import { PAGE_PARAM_KEY, PAGE_SIZE_PARAM_KEY } from '../../constants/route'
import { isEqualNullables } from '../common'

/**
 * Returns pagination page extracted from search params
 *
 * @param params - Search params
 */
export function getPageFromSearchParams(params) {
  return parseInt(params.get(PAGINATION_PAGE_PARAM), 10) || 1
}

/**
 * Returns pagination page size extracted from search params
 *
 * @param params - Search params
 */
export function getPageSizeFromSearchParams(params) {
  const pageSize = parseInt(params.get(PAGINATION_PAGE_SIZE_PARAM), 10)

  return PAGINATION_PAGE_SIZES.includes(pageSize)
    ? pageSize
    : PAGINATION_DEFAULT_PAGE_SIZE
}

/**
 * Returns location params for pagination
 *
 * @param pagination - Pagination
 */
export function getPaginationLocationParams({ page, pageSize }) {
  return {
    ...(page !== 1 ? { [PAGINATION_PAGE_PARAM]: page } : null),
    ...(pageSize !== PAGINATION_DEFAULT_PAGE_SIZE
      ? { [PAGINATION_PAGE_SIZE_PARAM]: pageSize }
      : null),
  }
}

/**
 * Checks if the passed `page` is valid pagination page
 *
 * @param page
 * @param value - Pagination value
 */
export function isValidPaginationPage(page, value) {
  const { count = 0, pageSize = 0 } = value ?? EMPTY_OBJECT
  const start = Number(page) * pageSize - pageSize

  return (
    Number.isSafeInteger(pageSize) &&
    pageSize > 0 &&
    Number.isSafeInteger(start) &&
    start >= 0 &&
    start < count
  )
}

/**
 * Checks if the passed pagination value is valid
 *
 * @param value - Pagination value
 */
export function isValidPagination(value) {
  return isValidPaginationPage(value?.page, value)
}

/**
 * Returns last pagination page
 *
 * @param pagination
 */
export function getLastPaginationPage(pagination) {
  return pagination.pageCount
}

/**
 * Checks if the first pagination is equal the second one
 *
 * @param pagination1 - The first pagination
 * @param pagination2 - The second pagination
 */
export function isEqualPagination(pagination1, pagination2) {
  return (
    pagination1 === pagination2 ||
    (pagination1.count === pagination2.count &&
      pagination1.pageCount === pagination2.pageCount &&
      pagination1.page === pagination2.page &&
      pagination1.pageSize === pagination2.pageSize)
  )
}

/**
 * Returns params for pagination to use inside API calls
 *
 * @param pagination - Pagination value
 */
export function getPaginationApiParams(pagination) {
  return {
    [PAGE_PARAM_KEY]: pagination.page,
    [PAGE_SIZE_PARAM_KEY]: pagination.pageSize,
  }
}

/**
 * Updates pagination value
 *
 * @param pagination - Pagination value
 * @param nextPaginationProps - Pagination props
 */
export function updatePagination(pagination, nextPaginationProps) {
  const nextPagination = pagination && { ...pagination, ...nextPaginationProps }

  return isEqualNullables(pagination, nextPagination, isEqualPagination)
    ? pagination
    : nextPagination
}

/**
 * Updates pagination value
 *
 * @param pagination - Pagination value
 * @param page - Pagination page
 */
export function updatePaginationPage(pagination, page) {
  return updatePagination(pagination, {
    page: clamp(page, 1, getLastPaginationPage(pagination)),
  })
}

/**
 * Updates pagination value from API response
 *
 * @param pagination - Pagination value
 * @param count - Items count
 */
export function updatePaginationCount(pagination, count) {
  return (
    pagination &&
    updatePaginationPage(
      { ...pagination, count: clamp(count, 0, Number.MAX_SAFE_INTEGER) },
      pagination.page
    )
  )
}
